<template>
  <div class="page-header">
    <h2>Gestion des Avis</h2>
  </div>

  <!-- Messages d'info -->
  <InfoBox
    v-if="infoMessage"
    v-model:open="infoOpen"
    :type="infoType"
    @close="infoOpen = false; infoMessage = ''"
  >
    {{ infoMessage }}
  </InfoBox>

  <!-- Filtres -->
  <div class="filters form-group">
    <!-- Filtres avancés -->
    <div class="btn-group">
      <div class="filters-group">
        <label>Utilisateur</label>
        <input v-model="filters.search" type="text" placeholder="Rechercher par nom..." />
      </div>
      <div class="filters-group">
        <label>Compagnie</label>
        <select v-model="filters.company">
          <option value="">Toutes</option>
          <option v-for="company in companies" :key="company" :value="company">
            {{ company }}
          </option>
        </select>
      </div>
      <div class="filters-group">
        <label>Visibilité</label>
        <select v-model="filters.visibility">
          <option value="">Tous</option>
          <option value="visible">👁️ Visible</option>
          <option value="hidden">🚫 Masqué</option>
        </select>
      </div>
      <div class="filters-group">
        <label>Date de début</label>
        <input v-model="filters.dateStart" type="date" @change="filters.period = 'custom'" />
      </div>
      <div class="filters-group">
        <label>Date de fin</label>
        <input v-model="filters.dateEnd" type="date" @change="filters.period = 'custom'" />
      </div>
    </div>

    <!-- Filtres rapides -->
    <div class="btn-group">
      <div>
        <button @click="setDatePeriod('current_month')" :class="['btn btn-chip', { 'is-active': filters.period === 'current_month' }]">Ce mois</button>
        <button @click="setDatePeriod('last_month')" :class="['btn btn-chip', { 'is-active': filters.period === 'last_month' }]">Mois dernier</button>
        <button @click="setDatePeriod('last_3_months')" :class="['btn btn-chip', { 'is-active': filters.period === 'last_3_months' }]">3 derniers mois</button>
        <button @click="setDatePeriod('last_6_months')" :class="['btn btn-chip', { 'is-active': filters.period === 'last_6_months' }]">6 derniers mois</button>
        <button @click="setDatePeriod('all')" :class="['btn btn-chip', { 'is-active': filters.period === 'all' }]">Tout afficher</button>
      </div>
    </div>

    <!-- Nombre de Résultats -->
    <div class="btn-group">
      <span class="badge badge-info">
        {{ filteredRatings.length }} avis trouvé(s) sur {{ allRatings.length }}
      </span>
      <button @click="resetFilters" class="btn btn-secondary">Réinitialiser</button>
    </div>
  </div>

  <!-- Tableau des avis -->
  <table v-if="!loading && allRatings.length > 0" class="table">
    <thead>
      <tr>
        <th @click="sortBy('overallRating')" class="th--sortable">Note <span class="sort-icon">{{ getSortIcon('overallRating') }}</span></th>
        <th @click="sortBy('user')" class="th--sortable">Utilisateur <span class="sort-icon">{{ getSortIcon('user') }}</span></th>
        <th @click="sortBy('company')" class="th--sortable">Compagnie <span class="sort-icon">{{ getSortIcon('company') }}</span></th>
        <th class="comment-column">Commentaire</th>
        <th @click="sortBy('createdAt')" class="th--sortable">Date <span class="sort-icon">{{ getSortIcon('createdAt') }}</span></th>
        <th>Actions</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="rating in visibleRatings" :key="rating.id" :class="['row', { 'row--disabled': !rating.isVisible }]">
        <td>
          <div class="rating-display">
            <span class="stars">{{ '⭐'.repeat(rating.overallRating) }}</span>
            <span class="rating-value">{{ rating.overallRating }}/5</span>
          </div>
        </td>
        <td>
          <span v-if="rating.userName">{{ rating.userName }}</span>
          <span v-else class="muted">Anonyme</span>
        </td>
        <td>{{ rating.companyName || 'N/A' }}</td>
        <td class="comment-cell">
          <div class="review-text-full">
            {{ rating.reviewText || '-' }}
          </div>
        </td>
        <td>{{ formatDate(rating.createdAt) }}</td>
        <td>
          <button
            v-if="rating.isVisible"
            @click="toggleVisibility(rating, false)"
            class="icon-btn icon-btn--warning"
            title="Masquer cet avis"
          >
            🚫
          </button>
          <button
            v-else
            @click="toggleVisibility(rating, true)"
            class="icon-btn icon-btn-success"
            title="Afficher cet avis"
          >
            👁️
          </button>
        </td>
      </tr>
    </tbody>
  </table>

  <!-- État vide -->
  <EmptyState
    v-if="!loading && filteredRatings.length === 0 && allRatings.length > 0"
    :is-empty="true"
    emptyMessage="Aucun avis ne correspond aux filtres"
  />

  <!-- Charger plus -->
  <div v-if="!loading && visibleRatings.length > 0 && visibleCount < filteredRatings.length" class="btn-group center">
    <button @click="loadMore" class="btn">- Charger plus -</button>
  </div>

  <!-- État vide global -->
  <EmptyState
    v-if="!loading && allRatings.length === 0"
    :is-empty="true"
    emptyMessage="Aucun avis disponible"
  />
</template>

<script setup>
import { config } from '@/config/env.js'
import { ref, onMounted, computed } from 'vue'
import { useToast } from 'vue-toast-notification'
import { useModalStore } from '@/stores/modalStore'
import InfoBox from '@/components/InfoBox.vue'
import EmptyState from '@/components/EmptyState.vue'

const emit = defineEmits(['loading'])
const toast = useToast()
const modalStore = useModalStore()
const infoOpen = ref(false)
const infoType = ref('info')
const infoMessage = ref('')

const allRatings = ref([])
const loading = ref(false)
const visibleCount = ref(20)
const ITEMS_STEP = 20

const sortKey = ref('createdAt')
const sortOrder = ref('desc')

const filters = ref({
  search: '',
  period: 'current_month',
  dateStart: '',
  dateEnd: '',
  company: '',
  visibility: ''
})

const companies = computed(() => {
  const uniqueCompanies = [...new Set(allRatings.value.map(r => r.companyName).filter(Boolean))]
  return uniqueCompanies.sort()
})

const setDatePeriod = (period) => {
  filters.value.period = period
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth()

  switch (period) {
    case 'current_month':
      filters.value.dateStart = new Date(year, month, 1).toISOString().split('T')[0]
      filters.value.dateEnd = new Date(year, month + 1, 0).toISOString().split('T')[0]
      break
    case 'last_month':
      filters.value.dateStart = new Date(year, month - 1, 1).toISOString().split('T')[0]
      filters.value.dateEnd = new Date(year, month, 0).toISOString().split('T')[0]
      break
    case 'last_3_months':
      filters.value.dateStart = new Date(year, month - 3, 1).toISOString().split('T')[0]
      filters.value.dateEnd = new Date(year, month + 1, 0).toISOString().split('T')[0]
      break
    case 'last_6_months':
      filters.value.dateStart = new Date(year, month - 6, 1).toISOString().split('T')[0]
      filters.value.dateEnd = new Date(year, month + 1, 0).toISOString().split('T')[0]
      break
    case 'all':
      filters.value.dateStart = ''
      filters.value.dateEnd = ''
      break
  }
}

const resetFilters = () => {
  filters.value = {
    search: '',
    period: 'current_month',
    dateStart: '',
    dateEnd: '',
    company: '',
    visibility: ''
  }
  setDatePeriod('current_month')
  visibleCount.value = ITEMS_STEP
}

const filteredRatings = computed(() => {
  return allRatings.value.filter(rating => {
    if (filters.value.search) {
      const searchTerm = filters.value.search.toLowerCase()
      const userName = (rating.userName || '').toLowerCase()
      if (!userName.includes(searchTerm)) return false
    }

    if (filters.value.visibility) {
      if (filters.value.visibility === 'visible' && !rating.isVisible) return false
      if (filters.value.visibility === 'hidden' && rating.isVisible) return false
    }

    if (filters.value.dateStart) {
      const ratingDate = new Date(rating.createdAt).toISOString().split('T')[0]
      if (ratingDate < filters.value.dateStart) return false
    }

    if (filters.value.dateEnd) {
      const ratingDate = new Date(rating.createdAt).toISOString().split('T')[0]
      if (ratingDate > filters.value.dateEnd) return false
    }

    if (filters.value.company) {
      if (rating.companyName !== filters.value.company) return false
    }

    return true
  })
})

const sortedRatings = computed(() => {
  const ratings = [...filteredRatings.value]
  ratings.sort((a, b) => {
    let aVal, bVal
    switch (sortKey.value) {
      case 'user': aVal = a.userName || ''; bVal = b.userName || ''; break
      case 'overallRating': aVal = a.overallRating || 0; bVal = b.overallRating || 0; break
      case 'company': aVal = a.companyName || ''; bVal = b.companyName || ''; break
      case 'createdAt': aVal = new Date(a.createdAt); bVal = new Date(b.createdAt); break
      default: return 0
    }
    if (aVal < bVal) return sortOrder.value === 'asc' ? -1 : 1
    if (aVal > bVal) return sortOrder.value === 'asc' ? 1 : -1
    return 0
  })
  return ratings
})

const visibleRatings = computed(() => sortedRatings.value.slice(0, visibleCount.value))

const sortBy = (key) => {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
}

const getSortIcon = (key) => sortKey.value !== key ? '⇅' : (sortOrder.value === 'asc' ? '↑' : '↓')
const loadMore = () => { if (visibleCount.value < sortedRatings.value.length) visibleCount.value += ITEMS_STEP }

onMounted(async () => {
  setDatePeriod('current_month')
  await loadRatings()
})

const loadRatings = async () => {
  loading.value = true
  emit('loading', true)

  try {
    const res = await fetch(config.apiUrl + '/ratings/admin/all', { credentials: 'include' })

    if (res.ok) {
      allRatings.value = await res.json()
      visibleCount.value = ITEMS_STEP
    } else {
      showInfo('error', 'Erreur lors du chargement des avis')
      toast.error('Erreur lors du chargement des avis')
    }
  } catch (err) {
    showInfo('error', err?.message || 'Erreur de connexion')
    toast.error('Erreur de connexion')
  } finally {
    loading.value = false
    emit('loading', false)
  }
}

const toggleVisibility = async (rating, newVisibility) => {
  const action = newVisibility ? 'afficher' : 'masquer'
  const { result } = modalStore.open({
    kind: 'confirm',
    title: `${newVisibility ? 'Afficher' : 'Masquer'} l'avis`,
    message: `Voulez-vous ${action} cet avis ?

📊 Note: ${rating.overallRating}/5
👤 Utilisateur: ${rating.userName || 'Anonyme'}
🏢 Compagnie: ${rating.companyName || 'N/A'}

💬 "${rating.reviewText || 'Aucun commentaire'}"`,
    confirmText: newVisibility ? 'Afficher' : 'Masquer',
    cancelText: 'Annuler'
  })

  const res = await result
  if (res.action === 'confirm') await moderateRating(rating.id, newVisibility)
}

const moderateRating = async (id, isVisible) => {
  loading.value = true
  emit('loading', true)

  try {
    const res = await fetch(`${config.apiUrl}/ratings/${id}/moderate`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ is_visible: isVisible })
    })

    if (res.ok) {
      await loadRatings()
      toast.success(isVisible ? 'Avis affiché' : 'Avis masqué')
      showInfo('success', isVisible ? 'Avis affiché' : 'Avis masqué')
    } else {
      const errData = await res.json()
      showInfo('error', errData.error || 'Erreur lors de la modération')
      toast.error(errData.error || 'Erreur lors de la modération')
    }
  } catch (err) {
    showInfo('error', err?.message || 'Erreur de connexion')
    toast.error('Erreur de connexion')
  } finally {
    loading.value = false
    emit('loading', false)
  }
}

const formatDate = (dateString) => {
  if (!dateString) return 'N/A'
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

const showInfo = (type, message) => {
  infoType.value = type
  infoMessage.value = message
  infoOpen.value = true
}
</script>

<style scoped>
.rating-display {
  display: flex;
  align-items: center;
  gap: 8px;
}

.stars {
  font-size: 1rem;
}

.rating-value {
  font-weight: 600;
  color: #666;
}

.comment-column {
  width: 40%;
  min-width: 300px;
}

.comment-cell {
  max-width: 600px;
}

.review-text-full {
  white-space: normal;
  word-wrap: break-word;
  line-height: 1.5;
  max-height: 120px;
  overflow-y: auto;
}

.row--disabled {
  opacity: 0.5;
  background-color: var(--color-grey-light);
}

.row--disabled td {
  text-decoration: line-through;
}

.muted {
  color: var(--color-text-secondary);
  font-style: italic;
}
</style>
